// Pure helpers behind the views (no DOM, no React), so the unit tests run them in Node: tree walks, book indents,
// list numbering, footnote order, comment quotes in a paragraph, colours, and the statistics' week and year.
import { journalPostsInTree, postContaining, type Block, type DailyWords, type Inline, type InlineStyle, type TreeNode } from '@writing-suite/core';
import { week as homeWeek } from '../../../core/src/home/index.ts';

// Trees ------------------------------------------------------------------------------------------------------------

export function findNode(nodes: TreeNode[], id: string): TreeNode | null {
  for (const n of nodes) {
    if (n.id === id) return n;
    const inner = findNode(n.children, id);
    if (inner) return inner;
  }
  return null;
}

/** The node and its ancestors, outermost first; null when it isn't in the tree. */
export function pathTo(nodes: TreeNode[], id: string, trail: TreeNode[] = []): TreeNode[] | null {
  for (const n of nodes) {
    if (n.id === id) return [...trail, n];
    const inner = pathTo(n.children, id, [...trail, n]);
    if (inner) return inner;
  }
  return null;
}

export const hasChildren = (n: TreeNode) => n.isFolder || n.children.length > 0;

/** Fragments under these nodes in reading order: each node's own text before its children's. */
export function readingFragments(nodes: TreeNode[], out: string[] = []): string[] {
  for (const n of nodes) {
    if (n.fragmentId) out.push(n.fragmentId);
    readingFragments(n.children, out);
  }
  return out;
}

export function flattenTree(nodes: TreeNode[], depth = 0, out: { node: TreeNode; depth: number }[] = []) {
  for (const n of nodes) {
    out.push({ node: n, depth });
    flattenTree(n.children, depth + 1, out);
  }
  return out;
}

/** The corkboard's cards for a selection: a folder's children, a scene's siblings (it selected), or the top level. */
/** `planChildren`: a folder's items, else a document's siblings. In a journal's posts (`journal`) a post or one of its
 *  sections shows the post's sections (its plan), and a post is never opened as a folder otherwise. */
export function boardFor(tree: TreeNode[], nodeId: string | null, journal = false): { folder: TreeNode | null; cards: TreeNode[]; selected: string | null } {
  const path = nodeId ? pathTo(tree, nodeId) : null;
  if (!path) return { folder: null, cards: tree, selected: null };
  const node = path[path.length - 1];
  if (journal) {
    const post = postContaining(node.id, journalPostsInTree(tree));
    const postNode = post ? pathTo(tree, post.nodeId)?.at(-1) : null;
    if (postNode) return { folder: postNode, cards: postNode.children, selected: node.id === postNode.id ? null : node.id };
  }
  if (node.children.length > 0 || node.isFolder) return { folder: node, cards: node.children, selected: null };
  const parent = path.length > 1 ? path[path.length - 2] : null;
  return { folder: parent, cards: parent ? parent.children : tree, selected: node.id };
}

// Colours ----------------------------------------------------------------------------------------------------------

/** "r g b" as label and entity colours are stored (0–1; 0–255 is accepted too) → a CSS colour, or null. */
export function cssColor(stored: string | null | undefined): string | null {
  if (!stored) return null;
  const parts = stored.trim().split(/[\s,]+/).map(Number);
  if (parts.length < 3 || parts.slice(0, 3).some((v) => !Number.isFinite(v) || v < 0)) return null;
  const scale = parts.slice(0, 3).every((v) => v <= 1) ? 255 : 1;
  const [r, g, b] = parts.slice(0, 3).map((v) => Math.round(Math.min(255, v * scale)));
  return `rgb(${r} ${g} ${b})`;
}

// Prose ------------------------------------------------------------------------------------------------------------

/**
 * Book indents (`EditorStyler.indentParagraphs`): a body paragraph's first line is indented, except the fragment's
 * first paragraph and one after a scene break, a quotation, an epigraph, a list or an aligned (`:::`) paragraph.
 */
export function bookIndents(blocks: Block[]): boolean[] {
  let previous: Block | null = null;
  return blocks.map((b) => {
    const t = b.kind.type;
    const indent =
      t === 'paragraph' &&
      b.alignment === null &&
      previous !== null &&
      previous.kind.type === 'paragraph' &&
      previous.alignment === null;
    if (t !== 'footnote') previous = b;
    return indent;
  });
}

/** Numbers shown for list items: a list starts at its first item's number and counts on (`startsList`). */
export function listNumbers(blocks: Block[], startsList: (block: Block, previous: Block | null) => boolean): (number | null)[] {
  let previous: Block | null = null;
  let n = 0;
  return blocks.map((b) => {
    if (b.kind.type !== 'listItem') {
      if (b.kind.type !== 'footnote') previous = b;
      return null;
    }
    n = startsList(b, previous) || previous?.kind.type !== 'listItem' ? b.kind.number : n + 1;
    previous = b;
    return n;
  });
}

/** Footnote numbers in order of first reference (numbering restarts in each fragment, as compile does). */
export function footnoteNumbers(blocks: Block[]): Map<string, number> {
  const out = new Map<string, number>();
  for (const b of blocks) {
    if (b.kind.type === 'footnote') continue;
    for (const i of b.inlines) if (i.type === 'footnoteReference' && !out.has(i.id)) out.set(i.id, out.size + 1);
  }
  for (const b of blocks) if (b.kind.type === 'footnote' && !out.has(b.kind.id)) out.set(b.kind.id, out.size + 1);
  return out;
}

export const plainOf = (inlines: Inline[]) => inlines.map((i) => (i.type === 'text' ? i.text : i.type === 'lineBreak' ? '\n' : '')).join('');

/** A text inline with the comment (index into the quotes given) whose quote covers it, if any. */
export type MarkedInline = Inline | { type: 'text'; text: string; style: InlineStyle; mark: number };

/**
 * Splits a paragraph's inlines so each comment's quote (its first occurrence in the paragraph's text, with curly and
 * straight quotes alike) is marked. Quotes that span paragraphs or aren't found are left unmarked.
 */
export function markQuotes(inlines: Inline[], quotes: string[]): MarkedInline[] {
  const fold = (s: string) => s.replace(/[‘’]/g, "'").replace(/[“”]/g, '"');
  const text = fold(plainOf(inlines));
  const ranges: { start: number; end: number; mark: number }[] = [];
  quotes.forEach((q, mark) => {
    const quote = fold(q.trim());
    if (quote.length < 2) return;
    const start = text.indexOf(quote);
    if (start >= 0 && !ranges.some((r) => start < r.end && start + quote.length > r.start)) ranges.push({ start, end: start + quote.length, mark });
  });
  if (!ranges.length) return inlines;
  const out: MarkedInline[] = [];
  let at = 0;
  for (const i of inlines) {
    if (i.type !== 'text') {
      out.push(i);
      at += i.type === 'lineBreak' ? 1 : 0;
      continue;
    }
    const end = at + i.text.length;
    const cuts = new Set<number>([at, end]);
    for (const r of ranges) for (const p of [r.start, r.end]) if (p > at && p < end) cuts.add(p);
    const points = [...cuts].sort((a, b) => a - b);
    for (let k = 0; k + 1 < points.length; k++) {
      const [s, e] = [points[k], points[k + 1]];
      const piece = i.text.slice(s - at, e - at);
      const r = ranges.find((r) => s >= r.start && e <= r.end);
      out.push(r ? { type: 'text', text: piece, style: i.style, mark: r.mark } : { type: 'text', text: piece, style: i.style });
    }
    at = end;
  }
  return out;
}

// Numbers and words ------------------------------------------------------------------------------------------------

const NUMBER = new Intl.NumberFormat('en-GB');
export const formatNumber = (n: number) => NUMBER.format(n);
export const formatWords = (n: number) => `${NUMBER.format(n)} ${n === 1 ? 'word' : 'words'}`;

const WORDS = ['no', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten'];
export const numberWord = (n: number) => WORDS[n] ?? NUMBER.format(n);

// Days -------------------------------------------------------------------------------------------------------------

/** `YYYY-MM-DD` of a date in this computer's time zone (the word log's days are the writer's). */
export function localDay(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

export function addDays(d: Date, n: number): Date {
  const out = new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
  return out;
}

export function wordsByDay(daily: DailyWords[]): Map<string, DailyWords> {
  const out = new Map<string, DailyWords>();
  for (const d of daily) {
    const had = out.get(d.day);
    out.set(d.day, had ? { day: d.day, added: had.added + d.added, removed: had.removed + d.removed } : d);
  }
  return out;
}

const WEEKDAY_SHORT = new Intl.DateTimeFormat('en-GB', { weekday: 'short' });

/** The last seven days' words (added) and the Library's sentence about them (`LibraryHome.week`, in the core). */
export function week(daily: DailyWords[], today = new Date()) {
  const w = homeWeek(daily, today);
  const days = w.days.map((d) => ({ day: d.day, label: WEEKDAY_SHORT.format(d.date), words: d.words }));
  return { days, total: days.reduce((n, d) => n + d.words, 0), sentence: w.sentence };
}

export interface GridDay {
  day: string;
  words: number;
  level: number;
}

/** `WritingHistory.years`: the calendar years with any writing (words written or cut), newest first. */
export function writingYears(daily: DailyWords[]): number[] {
  const years = new Set<number>();
  for (const d of daily) if (d.added > 0 || d.removed > 0) years.add(Number(d.day.slice(0, 4)));
  return [...years].filter(Number.isFinite).sort((a, b) => b - a);
}

/**
 * The year grid (`ActivityGrid`), Monday first: the 53 weeks ending with this one, or with `year` every week touching
 * that calendar year. Days after today, and outside the year, are null. Shades 1–4 come from the daily target (1, ¼,
 * ½, all of it) or else the quartiles of the shown days' writing. `days`: the days with words written.
 */
export function activityGrid(daily: DailyWords[], target: number | null, today = new Date(), year: number | null = null) {
  const byDay = wordsByDay(daily);
  const monday = (d: Date) => addDays(d, -((d.getDay() + 6) % 7));
  const todayKey = localDay(today);
  // The first column's Monday, the days shown (from–through, as keys), and the number of columns.
  let start: Date, from: string, through: string, columns: number;
  if (year === null) {
    start = addDays(monday(today), -52 * 7);
    from = localDay(start);
    through = todayKey;
    columns = 53;
  } else {
    const jan1 = new Date(year, 0, 1), dec31 = new Date(year, 11, 31);
    start = monday(jan1);
    from = localDay(jan1);
    through = localDay(dec31) < todayKey ? localDay(dec31) : todayKey;
    // The whole year's width, this year's later weeks empty.
    columns = Math.floor(Math.round((dec31.getTime() - start.getTime()) / 86_400_000) / 7) + 1;
  }
  const weeks: (GridDay | null)[][] = [];
  const values: number[] = [];
  for (let w = 0; w < columns; w++) {
    const column: (GridDay | null)[] = [];
    for (let d = 0; d < 7; d++) {
      const day = localDay(addDays(start, w * 7 + d));
      if (day < from || day > through) {
        column.push(null);
        continue;
      }
      const words = byDay.get(day)?.added ?? 0;
      if (words > 0) values.push(words);
      column.push({ day, words, level: 0 });
    }
    weeks.push(column);
  }
  const sorted = values.sort((a, b) => a - b);
  const thresholds =
    target && target > 0
      ? [1, Math.floor(target / 4), Math.floor(target / 2), target]
      : sorted.length
        ? [1, sorted[Math.floor(sorted.length / 4)], sorted[Math.floor(sorted.length / 2)], sorted[Math.floor((sorted.length * 3) / 4)]]
        : [1, 2, 3, 4];
  const level = (words: number) => {
    if (words < 1) return 0;
    let at = 0;
    thresholds.forEach((t, i) => {
      if (words >= t) at = i + 1;
    });
    return Math.max(1, at);
  };
  for (const column of weeks) for (const d of column) if (d) d.level = level(d.words);
  return { weeks, thresholds, year: sorted.reduce((n, v) => n + v, 0), days: sorted.length, start: localDay(start) };
}

// Series -----------------------------------------------------------------------------------------------------------

/** `Series.key`: spellings differing only in case, accents or spacing are one series. */
export const seriesKey = (name: string) =>
  name
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();

/** `Series.groups`: series in name order (named as most of their projects spell it), then the projects in none. */
export function seriesGroups<T>(items: T[], seriesOf: (t: T) => string | null): { series: string | null; items: T[] }[] {
  const groups = new Map<string, T[]>();
  const none: T[] = [];
  for (const t of items) {
    const s = seriesOf(t)?.trim();
    if (!s) none.push(t);
    else groups.set(seriesKey(s), [...(groups.get(seriesKey(s)) ?? []), t]);
  }
  const named = [...groups.values()].map((members) => {
    const counts = new Map<string, number>();
    for (const m of members) counts.set(seriesOf(m)!.trim(), (counts.get(seriesOf(m)!.trim()) ?? 0) + 1);
    const capitals = (s: string) => (s.match(/\p{Lu}/gu) ?? []).length;
    const name = [...counts].sort((a, b) => b[1] - a[1] || capitals(b[0]) - capitals(a[0]) || a[0].localeCompare(b[0]))[0][0];
    return { series: name as string | null, items: members };
  });
  named.sort((a, b) => a.series!.localeCompare(b.series!, 'en-GB', { sensitivity: 'base' }));
  return none.length ? [...named, { series: null, items: none }] : named;
}
